"use client";

import EditableText from "@/app/(components)/generics/EditableText/EditableText";
import OfferRequired from "@/app/(components)/specifics/tools/Offers/components/OfferRequired/OfferRequired";
import { useCanManageWorkspace } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import { updateProject } from "@/app/actions/project/update/action";
import { useFetchProjectOverview } from "@/app/hooks/fetchs/project/useFetchProjectOverview";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { PageParams } from "@/app/types/common/DefaultProps";
import { ProjectLite } from "@/app/types/project/ProjectLite";
import { ULogger } from "@/app/utils/ULogger";
import { Limits } from "@/app/vars/Limits";
import BrowserFrame from "./components/BrowserFrame/BrowserFrame";
import Dashboard from "./components/Dashboard/Dashboard";
import { ProjectField } from "./Overview.logic";
import styles from "./Overview.module.css";

type Props = {
  params: PageParams;
  project: ProjectLite;
  enabled: boolean;
};

export default function Overview(props: Props) {
  const T = useT();
  const { params, project, enabled } = props;

  const canEdit = useCanManageWorkspace();
  const { data: stats } = useFetchProjectOverview(project.id, enabled);

  const handleSave = async (field: ProjectField, value: string) => {
    try {
      const updated = await updateProject(project.id, { [field]: value || undefined });
      if (updated) {
        ProjectStore.getState().updateProject(updated);
      }
    } catch (err) {
      ULogger.error("OverviewTab save failed", err);
    }
  };

  const needsOffer = !!stats && stats.offers === 0;

  const description = (
    <EditableText
      value={project.description || ""}
      maxLength={Limits.MAX_PROJECT_DESCRIPTION}
      onSave={(v) => handleSave(ProjectField.DESCRIPTION, v)}
      placeholder={T(TKey.PROJECT_DESCRIPTION_PLACEHOLDER) as string}
      multiline
      fitContent
      fullWidthOnEdit
      disabled={!canEdit}
      textClassName={styles.lead}
      inputClassName={styles.lead}
    />
  );

  return (
    <BrowserFrame
      link={project.link}
      canEdit={canEdit}
      linkMaxLength={Limits.MAX_LINK}
      onSaveLink={(v) => handleSave(ProjectField.LINK, v)}
    >
      {needsOffer ? (
        <div className={styles.container}>
          {description}

          <OfferRequired params={params} />
        </div>
      ) : (
        <Dashboard
          params={params}
          projectId={project.id}
          stats={stats}
          enabled={enabled}
          description={description}
        />
      )}
    </BrowserFrame>
  );
}
